import React, { createElement } from 'react'
import { graphql } from 'gatsby'
import Breaker from '../components/breaker'
import CtaBold from '../components/cta-bold'
import CtaEvents from '../components/cta-events'
import CtaSimple from '../components/cta-simple'
import DescriptionList from '../components/description-list'
import FeatureCards from '../components/feature-cards'
import FeatureColumns from '../components/feature-columns'
import FeatureImage from '../components/feature-image'
import FeatureTiles from '../components/feature-tiles'
import FilterAppearance from '../components/filter-appearance'
import FilterArticle from '../components/filter-article'
import FilterExhibitor from '../components/filter-exhibitor'
import FilterProgram from '../components/filter-program'
import FilterSeminar from '../components/filter-seminar'
import FilterShow from '../components/filter-show'
import Form from '../components/form'
import Hero from '../components/hero'
import HeroFeature from '../components/hero-feature'
import Pricing from '../components/pricing'
import Wysiwyg from '../components/wysiwyg'
import ContentNodeContext from '../contexts/content-node'

const sections = {
  Breaker,
  CtaBold,
  CtaEvents,
  CtaSimple,
  DescriptionList,
  FeatureCards,
  FeatureColumns,
  FeatureImage,
  FeatureTiles,
  FilterAppearance,
  FilterArticle,
  FilterExhibitor,
  FilterProgram,
  FilterSeminar,
  FilterShow,
  Form,
  Hero,
  HeroFeature,
  Pricing,
  Wysiwyg
}

export const query = graphql`
  fragment Link on WpAcfLink {
    target
    title
    url
  }

  fragment Image on WpMediaItem {
    altText
    height
    sizes
    sourceUrl
    srcSet
    width
  }

  fragment Title on WpNodeWithTitle {
    title
  }

  fragment FeaturedImage on WpNodeWithFeaturedImage {
    featuredImage {
      node {
        ...Image
      }
    }
  }

  fragment MetaAppearance on WpContentNode_Acf_Meta {
    appearance {
      locations {
        ... on WpLocation {
          title
          acf {
            meta {
              ...MetaLocation
            }
          }
        }
      }
    }
  }

  fragment MetaArticle on WpContentNode_Acf_Meta {
    article {
      locations {
        ... on WpLocation {
          title
          acf {
            meta {
              ...MetaLocation
            }
          }
        }
      }
    }
  }

  fragment MetaExhibitor on WpContentNode_Acf_Meta {
    exhibitor {
      locations {
        ... on WpLocation {
          title
          acf {
            meta {
              ...MetaLocation
            }
          }
        }
      }
    }
  }

  fragment MetaLocation on WpContentNode_Acf_Meta {
    location {
      dates
    }
  }

  fragment MetaProgram on WpContentNode_Acf_Meta {
    program {
      locations {
        ... on WpLocation {
          title
          acf {
            meta {
              ...MetaLocation
            }
          }
        }
      }
    }
  }

  fragment SectionBreaker on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_Breaker {
      breakerContent {
        heading
        description
      }
    }
  }

  fragment SectionCtaBold on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_CtaBold {
      ctaBoldContent {
        links {
          link {
            ...Link
          }
        }
      }
      ctaBoldDesign {
        theme
      }
    }
  }

  fragment SectionCtaEvents on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_CtaEvents {
      ctaEventsContent {
        heading
        link {
          ...Link
        }
        events {
          location {
            ... on WpLocation {
              uri
              title
            }
          }
          dates
        }
      }
      ctaEventsSponsors {
        logos {
          image {
            ...Image
          }
          link {
            ...Link
          }
        }
      }
    }
  }

  fragment SectionCtaSimple on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_CtaSimple {
      ctaSimpleContent {
        heading
        description
        link {
          ...Link
        }
      }
      ctaSimpleDesign {
        theme
      }
    }
  }

  fragment SectionDescriptionList on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_DescriptionList {
      descriptionListContent {
        heading
        description
        groups {
          name
          value
        }
      }
    }
  }

  fragment SectionFeatureCards on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FeatureCards {
      featureCardsHeader {
        text {
          banner
          heading
          description
        }
        sponsor {
          intro
          image {
            ...Image
          }
          link {
            ...Link
          }
        }
      }
      featureCardsCards {
        post {
          ... on WpContentNode {
            acf {
              meta {
                card {
                  content {
                    heading
                    subheading
                    description
                    link {
                      ...Link
                    }
                  }
                  media {
                    image {
                      ...Image
                    }
                    video
                  }
                }
              }
            }
          }
        }
        content {
          heading
          subheading
          description
          link {
            ...Link
          }
        }
        media {
          image {
            ...Image
          }
          video
        }
      }
      featureCardsFooter {
        link {
          ...Link
        }
      }
      featureCardsDesign {
        theme
        columns
      }
    }
  }

  fragment SectionFeatureColumns on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FeatureColumns {
      featureColumnsContent {
        columns {
          links {
            icons {
              name
            }
          }
          media {
            caption
            video
            image {
              ...Image
            }
          }
          text {
            description
            heading
          }
        }
      }
    }
  }

  fragment SectionFeatureImage on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FeatureImage {
      featureImageContent {
        kicker
        heading
        description
        links {
          buttons {
            link {
              ...Link
            }
          }
        }
      }
      featureImageMedia {
        image {
          ...Image
        }
        video
      }
      featureImageDesign {
        theme
        flip
      }
    }
  }

  fragment SectionFilterAppearance on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterAppearance {
      filterAppearanceHeader {
        heading
        description
      }
    }
  }

  fragment SectionFilterArticle on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterArticle {
      filterArticleHeader {
        heading
        description
      }
    }
  }

  fragment SectionFilterExhibitor on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterExhibitor {
      filterExhibitorHeader {
        heading
        description
      }
    }
  }

  fragment SectionFilterProgram on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterProgram {
      filterProgramHeader {
        heading
        description
      }
    }
  }

  fragment SectionFilterSeminar on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterSeminar {
      filterSeminarHeader {
        heading
        description
      }
    }
  }

  fragment SectionFilterShow on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FilterShow {
      filterShowHeader {
        heading
        description
      }
    }
  }

  fragment SectionForm on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_Form {
      formText {
        heading
        description
      }
      formForm
    }
  }

  fragment SectionFeatureTiles on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_FeatureTiles {
      featureTilesContent {
        heading
        description
        ctas {
          buttons {
            link {
              ...Link
            }
          }
        }
      }
      featureTilesTiles {
        image {
          ...Image
        }
        link {
          ...Link
        }
      }
    }
  }

  fragment SectionHero on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_Hero {
      heroText {
        kicker
        heading
        description
      }
      heroLinks {
        ctas {
          buttons {
            link {
              ...Link
            }
          }
        }
      }
      heroMedia {
        background {
          image {
            ...Image
          }
          video
        }
        logos {
          logos {
            image {
              ...Image
            }
            link {
              ...Link
            }
          }
        }
      }
    }
  }

  fragment SectionHeroFeature on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_HeroFeature {
      heroFeatureText {
        kicker
        heading
        description
      }
      heroFeatureLinks {
        social {
          icons {
            name
            link {
              ...Link
            }
          }
        }
      }
      heroFeatureMedia {
        foreground {
          image {
            ...Image
          }
          video
        }
      }
    }
  }

  fragment SectionPricing on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_Pricing {
      pricingDesign {
        layout
      }
      pricingHeader {
        description
        heading
        disclaimer
      }
      pricingOffers {
        category
        location {
          ... on WpLocation {
            slug
            title
            acf {
              meta {
                ...MetaLocation
              }
            }
          }
        }
        description
        features {
          description
        }
        heading
        kicker
        price
        link {
          ...Link
        }
        subheading
      }
    }
  }

  fragment SectionWysiwyg on WpContentNode_Acf_Content_Sections {
    ... on WpContentNode_Acf_Content_Sections_Wysiwyg {
      fieldGroupName
      wysiwygHeader {
        kicker
        heading
      }
      wysiwygContent {
        blocks {
          __typename
          ... on WpContentNode_Acf_Content_Sections_Wysiwyg_WysiwygContent_Blocks_Media {
            video
            image {
              ...Image
            }
            caption
          }
          ... on WpContentNode_Acf_Content_Sections_Wysiwyg_WysiwygContent_Blocks_Text {
            text
          }
        }
      }
    }
  }

  query ($id: String) {
    wp {
      allSettings {
        generalSettingsTitle
      }
    }
    wpContentNode(id: { eq: $id }) {
      ...Title
      contentTypeName
      acf {
        meta {
          ...MetaAppearance
          ...MetaArticle
          ...MetaExhibitor
          ...MetaLocation
          ...MetaProgram
        }
        content {
          sections {
            __typename
            ...SectionBreaker
            ...SectionCtaBold
            ...SectionCtaEvents
            ...SectionCtaSimple
            ...SectionDescriptionList
            ...SectionFeatureCards
            ...SectionFeatureColumns
            ...SectionFeatureImage
            ...SectionFeatureTiles
            ...SectionFilterAppearance
            ...SectionFilterArticle
            ...SectionFilterExhibitor
            ...SectionFilterProgram
            ...SectionFilterSeminar
            ...SectionFilterShow
            ...SectionForm
            ...SectionHero
            ...SectionHeroFeature
            ...SectionPricing
            ...SectionWysiwyg
          }
        }
      }
    }
  }
`

function uncapitalizeFirstLetter([first = '', ...rest]) {
  return [first.toLowerCase(), ...rest].join('')
}

export default function ContentNode({ data: { wpContentNode } }) {
  return (
    <ContentNodeContext.Provider value={wpContentNode}>
      <>
        {wpContentNode.acf?.content?.sections?.map(
          ({ __typename, ...props }, key) => {
            const name = __typename.split('_').pop()

            for (const oldKey in props) {
              const value = props[oldKey]
              const key = uncapitalizeFirstLetter(
                oldKey.replace(
                  new RegExp(`^${uncapitalizeFirstLetter(name)}`),
                  ''
                )
              )

              props[key] = value

              delete props[oldKey]
            }

            return createElement(sections[name], { key, ...props })
          }
        )}
      </>
    </ContentNodeContext.Provider>
  )
}

export const Head = ({
  data: {
    wp: {
      allSettings: { generalSettingsTitle }
    },
    wpContentNode: { title }
  }
}) => {
  return (
    <>
      <title>
        {generalSettingsTitle} - {title}
      </title>
    </>
  )
}
